import * as React from "react"
import { type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import { SourceAccountMetricComponentTitle } from "src/frontend/components/Shared/Layout/Components/Headers/ComponentEntityIcons"
import { type ComponentSize } from "src/frontend/components/Shared/Layout/ComponentSize"
import {
  type MatchedComponent,
  type SizingProps,
} from "src/frontend/components/Shared/Layout/types"
import { MetricBarChartComponent } from "./MetricBarChartComponent"
import { type MetricSeries, type MetricValue } from "./toMetricSeries"

/*
  STYLES
*/

/*
  INTERFACES
*/

interface MetricComponentProps extends SizingProps {
  metricSeries: MetricSeries
  component: MatchedComponent<"sourceAccountMetric">
  componentSize: ComponentSize
  origin: IntervalOrigin
  skipAnimations: boolean
  loading: boolean
  title?: string
}

/*
  COMPONENTS
*/

export const SourceAccountMetricComponent: React.FC<MetricComponentProps> = ({
  metricSeries,
  component,
  componentSize,
  origin,
  height,
  skipAnimations,
  loading,
  title,
}) => {
  const {
    config: { sourceAccountMetric: config },
  } = component

  const period = React.useMemo(
    () => (config?.origin ? dateTimeHelper.periodFromIntervalOrigin(config.origin) : null),
    [config?.origin]
  )

  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(undefined)

  const onMouseOver = React.useCallback((value: MetricValue, index: number) => {
    setSelectedIndex(index)
  }, [])

  const onMouseOut = React.useCallback((value?: MetricValue) => {
    setSelectedIndex(undefined)
  }, [])

  const metricTitle = (
    <SourceAccountMetricComponentTitle
      componentSize={componentSize}
      iconObjectName={metricSeries.iconObjectName}
      title={title}
    />
  )

  if (!period) {
    return null
  }

  return (
    <MetricBarChartComponent
      componentSize={componentSize}
      title={metricTitle}
      icon={undefined}
      metricSeries={metricSeries}
      origin={origin}
      loading={loading}
      height={height}
      skipAnimations={skipAnimations}
      onClick={undefined}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      selectedIndex={selectedIndex}
    />
  )
}
